import React, { useEffect, useState, useMemo} from 'react'
import axios from 'axios'
import { BASE_URL } from '../../utils/constants';
import { MdOutlinePendingActions } from "react-icons/md";
import { MdExitToApp } from "react-icons/md";
import { Table, Button } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import { useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import _ from "underscore"
import { AiFillCaretUp, AiFillCaretDown } from "react-icons/ai";
import Pagination from '../../components/Pagination/Pagination';


import "./SurgeonTasks.scss"

const studyMap = {
  "shoulder arthroscopy": "SAs",
  "shoulder arthroplasty": "SAp",
  "elbow": "E"
}

const SurgeonTasks = ({ user, logUserOut }) => {
  const [loading, setLoading] = useState(false)
  const [openSurgeonTasks, setOpenSurgeonTasks] = useState([])
  const [sorting, setSorting] = useState([{ field: 'customPatientId', ascending: true }])
  const navigate = useNavigate();
  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  }

  // New state variables for UI-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  useEffect(() => {
    const getOpenSurgeonTasks = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${BASE_URL}/api/v1/surgeon/open-tasks/`, config)
        setOpenSurgeonTasks(response.data.openSurgeonTasks)
        setLoading(false)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getOpenSurgeonTasks()
  }, [])

  // Multi-sort function
  const multiSort = (a, b) => {
    for (let sortRule of sorting) {
      const { field, ascending } = sortRule;
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle date fields
      if (field === 'eventDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return ascending ? -1 : 1;
      if (aValue > bValue) return ascending ? 1 : -1;
    }
    return 0;
  };

  // Sort the openSurgeonTasks using multi-sort
  const sortedData = useMemo(() => {
    const dataToSort = openSurgeonTasks;
    return [...dataToSort].sort(multiSort);
  }, [sorting, openSurgeonTasks])

  const handleSort = (field, event) => {
    const isShiftPressed = event.shiftKey;
    const isCtrlPressed = event.ctrlKey || event.metaKey;

    if (isCtrlPressed) {
      setSorting([{ field: 'customPatientId', ascending: true }]);
      return;
    }

    setSorting(prevSorting => {
      const existingSortIndex = prevSorting.findIndex(s => s.field === field);
      
      if (existingSortIndex > -1) {
        return prevSorting.map((sortItem, index) => {
          if (index === existingSortIndex) {
            return { ...sortItem, ascending: !sortItem.ascending };
          }
          return sortItem;
        });
      } else {
        if (isShiftPressed) {
          return [...prevSorting, { field, ascending: true }];
        } else {
          return [{ field, ascending: true }];
        }
      }
    });
    setCurrentPage(1); // Reset to page 1 when sorting changes
  };

  const renderSortIndicator = (field) => {
    const sortIndex = sorting.findIndex(s => s.field === field);
    if (sortIndex === -1) return "";
    
    const { ascending } = sorting[sortIndex];
    return (
      <>
        {ascending ? <AiFillCaretUp /> : <AiFillCaretDown />}
        {sorting.length > 1 && <span className="sort-priority">{sortIndex + 1}</span>}
      </>
    );
  };

  const resetSorting = () => {
    setSorting([{ field: 'customPatientId', ascending: true }]);
  };

  // Logic to handle UI-side pagination
  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedData.slice(startIndex, endIndex);


  return (
    <div className="open-surgeon-tasks">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <div className="open-surgeon-tasks-header">
            <h2 className="logo"><MdOutlinePendingActions /> Open Surgeon Tasks</h2>
            {sorting.length > 1 && (
              <Button variant="outline-primary" size="sm" onClick={resetSorting} className="reset-sort-btn">
                Reset Sort
              </Button>
            )}
          </div>
          <div className='open-surgeon-tasks-table'>
            <Table striped bordered={false} hover>
              <thead>
                <tr>
                  <th className='custom-id' onClick={(e) => handleSort("customPatientId", e)}>Patient ID {renderSortIndicator("customPatientId")}</th>
                  <th className='study' onClick={(e) => handleSort("study", e)}>Study {renderSortIndicator("study")}</th>
                  <th className='event-date' onClick={(e) => handleSort("eventDate", e)}>Event Date {renderSortIndicator("eventDate")}</th>
                  <th className='actions'>Actions</th>
                  <th className='action-buttons'></th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? currentItems.map((openSurgeonTask, index) => {
                  return (
                    <tr key={index}>
                      <td className="custom-id">{openSurgeonTask.customPatientId}</td>
                      <td className="study">{studyMap[openSurgeonTask.study]} ({openSurgeonTask.treatmentSide[0]})</td>
                      <td className="event-date">{moment(openSurgeonTask.eventDate).format("DD/MM/YYYY")}</td>
                      <td className="actions">
                        <Link to={`/patients/${openSurgeonTask.customPatientId}/events/surgery/${openSurgeonTask.eventId}`}>View Surgery</Link></td>
                      <td className='action-buttons'>
                        <div>
                          <Button variant="primary" onClick={() => navigate(`/patients/${openSurgeonTask.customPatientId}`)}><MdExitToApp /></Button>
                        </div>
                      </td>
                    </tr>
                  )
                }) : <tr><td colSpan={100} style={{textAlign: "center"}}>100% compliance rate</td></tr>}
              </tbody>
            </Table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </>)}    
    </div>
  )
}

export default SurgeonTasks